import React, { useRef, useState } from "react";
import { Dropdown, Button, Modal, message, Input, Typography } from "antd";
import { RobotOutlined, DownOutlined, FileTextOutlined, ContainerOutlined, StopOutlined, MoreOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import apInvoicePipelineApi from "../../services/apInvoicePipelineApi";
import { getPerIdsPipelineActions } from "../../APReconciliationWorkbench/pipeline";
import OcrRetryModeNotice, {
    getOcrModeRecoveryAction,
    getOcrRetryOverride,
    getOcrRetryPresentation,
    isHighResolutionPdfOcrPreferred,
    OCR_RETRY_INTENT,
} from "../../APReconciliationWorkbench/components/OcrRetryModeNotice";
import OcrDpiSlider, { OCR_DPI_DEFAULT, resolveOcrDpi } from "../../APReconciliationWorkbench/components/OcrDpiSlider";

const { TextArea } = Input;
const { Text } = Typography;

const InvoiceDataSourceActionMenu = ({
    record,
    onSuccess,
    triggerType = "button", // "button" | "icon" | "link"
    buttonText = "重新智能分析",
    isReadOnly = false,
}) => {
    const [reparseModalVisible, setReparseModalVisible] = useState(false);
    const [aiSettlementModalVisible, setAiSettlementModalVisible] = useState(false);
    const [customRequirement, setCustomRequirement] = useState("");
    const [ocrRetryIntent, setOcrRetryIntent] = useState(OCR_RETRY_INTENT.INHERIT);
    const [ocrRetryDpi, setOcrRetryDpi] = useState(OCR_DPI_DEFAULT);
    const [ocrRetryDpiTouched, setOcrRetryDpiTouched] = useState(false);
    const [ocrSubmitting, setOcrSubmitting] = useState(false);
    const ocrSubmittingRef = useRef(false);

    // 判断是否为已拆分的父文件（需要后端返回has_split_children字段）
    // TODO: 等待后端实现has_split_children字段后启用
    const isParentOfSplitFiles = record.has_split_children === true;

    // 重新解析
    const handleReparse = (intent = OCR_RETRY_INTENT.INHERIT) => {
        if (ocrSubmittingRef.current) return;
        const inheritsHighResolution = intent === OCR_RETRY_INTENT.INHERIT && isHighResolutionPdfOcrPreferred(record);
        setCustomRequirement("");
        setOcrRetryIntent(intent);
        setOcrRetryDpi(intent === OCR_RETRY_INTENT.HIGH_RESOLUTION || inheritsHighResolution ? resolveOcrDpi(record) : OCR_DPI_DEFAULT);
        setOcrRetryDpiTouched(false);
        setReparseModalVisible(true);
    };

    // 确认重新解析
    const handleConfirmReparse = async () => {
        if (ocrSubmittingRef.current) return;
        ocrSubmittingRef.current = true;
        setOcrSubmitting(true);
        try {
            const modeOverride = getOcrRetryOverride(ocrRetryIntent);
            const inheritHighResolution = ocrRetryIntent === OCR_RETRY_INTENT.INHERIT && isHighResolutionPdfOcrPreferred(record);
            const retryArgs = [record.id, "ocr", customRequirement || undefined, modeOverride];
            if (ocrRetryIntent === OCR_RETRY_INTENT.HIGH_RESOLUTION || (inheritHighResolution && ocrRetryDpiTouched)) retryArgs.push(ocrRetryDpi);
            const response = await apInvoicePipelineApi.retryFromStage(...retryArgs);
            message.success(response.message || "OCR 识别任务已提交");
            setReparseModalVisible(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            message.error("OCR 识别失败: " + (error.response?.data?.detail || error.message));
        } finally {
            ocrSubmittingRef.current = false;
            setOcrSubmitting(false);
        }
    };

    const handleCancelReparse = () => {
        if (ocrSubmittingRef.current) return;
        setReparseModalVisible(false);
    };

    // 重新执行AI对账
    const handleAiSettlement = () => {
        setAiSettlementModalVisible(true);
    };

    // 确认重新执行AI对账
    const handleConfirmAiSettlement = async () => {
        try {
            const response = await apInvoicePipelineApi.retryFromStage(record.id, "parser");
            message.success(response.message || "制表校验任务已提交");
            setAiSettlementModalVisible(false);
            if (onSuccess) onSuccess();
        } catch (error) {
            message.error("制表校验重试失败: " + (error.response?.data?.detail || error.message));
        }
    };

    // 重新分摊航班
    const handleAllocation = () => {
        Modal.confirm({
            title: "重新分摊航班",
            content: "确定要重新执行分摊航班吗？这会基于现有制表校验数据重新匹配费用与航班。",
            onOk: async () => {
                try {
                    const response = await apInvoicePipelineApi.retryFromStage(record.id, "allocation");
                    message.success(response.message || "分摊航班任务已提交");
                    if (onSuccess) onSuccess();
                } catch (error) {
                    message.error("分摊航班失败: " + (error.response?.data?.detail || error.message));
                }
            },
        });
    };

    // 重新验证航班
    const handleValidation = () => {
        Modal.confirm({
            title: "重新验证航班",
            content: "确定要重新执行验证航班吗？这会基于现有分摊航班结果重新运行验证规则。",
            onOk: async () => {
                try {
                    const response = await apInvoicePipelineApi.retryFromStage(record.id, "validation");
                    message.success(response.message || "验证航班任务已提交");
                    if (onSuccess) onSuccess();
                } catch (error) {
                    message.error("验证航班失败: " + (error.response?.data?.detail || error.message));
                }
            },
        });
    };

    // 强制停止
    const handleStop = async () => {
        try {
            const response = await apInvoicePipelineApi.stopPipeline(record.id);
            message.success(response.message || "已发送强制停止指令");
            if (onSuccess) onSuccess();
        } catch (error) {
            message.error("强制停止失败: " + (error.response?.data?.detail || error.message));
        }
    };

    // Agent重试菜单处理
    const handleMenuClick = ({ key }) => {
        switch (key) {
            case "ocr":
                handleReparse();
                break;
            case "ocr_mode_recovery":
                handleReparse(ocrRecoveryAction.intent);
                break;
            case "parser":
                handleAiSettlement();
                break;
            case "allocation":
                handleAllocation();
                break;
            case "validation":
                handleValidation();
                break;
            case "stop":
                handleStop();
                break;
            default:
                break;
        }
    };

    const commonDisabled = isReadOnly || isParentOfSplitFiles;
    const iconMap = {
        ocr: <FileTextOutlined />,
        parser: <ContainerOutlined />,
        allocation: <RobotOutlined />,
        validation: <SafetyCertificateOutlined />,
        stop: <StopOutlined />,
    };
    const availableActions = getPerIdsPipelineActions(record.status);
    const ocrRecoveryAction = getOcrModeRecoveryAction(record);
    const menuItems = [];
    const triggerDisabled = commonDisabled || availableActions.length === 0;

    availableActions.forEach((action, index) => {
        if (action.key === "stop" && index > 0) {
            menuItems.push({ type: "divider" });
        }
        menuItems.push({
            key: action.key,
            label: action.label,
            icon: iconMap[action.key],
            danger: action.danger,
            disabled: commonDisabled,
        });
        if (action.stageId === "ocr") {
            menuItems.push({
                key: ocrRecoveryAction.key,
                label: ocrRecoveryAction.label,
                icon: <FileTextOutlined />,
                disabled: commonDisabled,
            });
        }
    });

    const ocrRetryPresentation = getOcrRetryPresentation(ocrRetryIntent, record);
    const showOcrDpiSlider = ocrRetryIntent === OCR_RETRY_INTENT.HIGH_RESOLUTION
        || (ocrRetryIntent === OCR_RETRY_INTENT.INHERIT && isHighResolutionPdfOcrPreferred(record));

    const renderTrigger = () => {
        if (triggerType === "icon") {
            return <Button type="text" size="small" icon={<MoreOutlined />} disabled={triggerDisabled} onClick={(e) => e.stopPropagation()} />;
        }
        if (triggerType === "link") {
            return (
                <Button type="link" size="small" disabled={triggerDisabled} onClick={(e) => e.stopPropagation()}>
                    {buttonText} <DownOutlined />
                </Button>
            );
        }
        return (
            <Button icon={<RobotOutlined />} disabled={triggerDisabled} onClick={(e) => e.stopPropagation()}>
                {buttonText} <DownOutlined />
            </Button>
        );
    };

    return (
        <>
            <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={["click"]} disabled={triggerDisabled}>
                {renderTrigger()}
            </Dropdown>

            {/* OCR 识别模态框 */}
            <Modal
                title={ocrRetryPresentation.title}
                open={reparseModalVisible}
                onOk={handleConfirmReparse}
                onCancel={handleCancelReparse}
                okText="开始识别"
                cancelText="取消"
                confirmLoading={ocrSubmitting}
                okButtonProps={{ disabled: ocrSubmitting }}
                cancelButtonProps={{ disabled: ocrSubmitting }}
                closable={!ocrSubmitting}
                maskClosable={!ocrSubmitting}
                keyboard={!ocrSubmitting}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ marginBottom: 16 }}>
                    <Text>确定要重新 OCR 识别这个文件吗？系统将重新读取原始文件内容。</Text>
                </div>
                <OcrRetryModeNotice intent={ocrRetryIntent} ocrOptions={record} pdfRenderDpi={ocrRetryDpi} />
                {showOcrDpiSlider && (
                    <OcrDpiSlider
                        value={ocrRetryDpi}
                        onChange={(nextDpi) => {
                            setOcrRetryDpi(nextDpi);
                            setOcrRetryDpiTouched(true);
                        }}
                        disabled={ocrSubmitting}
                    />
                )}
                <div>
                    <Text strong>自定义识别要求（可选）：</Text>
                    <TextArea rows={3} value={customRequirement} onChange={(e) => setCustomRequirement(e.target.value)} placeholder="例如：重点关注燃油费用项目，忽略小额杂费..." style={{ marginTop: 8 }} disabled={ocrSubmitting} />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        自定义要求将传递给识别 Agent，帮助更准确地提取信息
                    </Text>
                </div>
            </Modal>

            {/* 制表校验重试模态框 */}
            <Modal title="重新制表校验" open={aiSettlementModalVisible} onOk={handleConfirmAiSettlement} onCancel={() => setAiSettlementModalVisible(false)} okText="开始处理" cancelText="取消" onClick={(e) => e.stopPropagation()}>
                <div>
                    <Text>确定要重新执行制表校验吗？系统将基于当前 OCR 结果重新生成账单表格并校验金额。</Text>
                </div>
            </Modal>
        </>
    );
};

export default InvoiceDataSourceActionMenu;
